import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, formatCurrency } from '../../utils/api'
import { dealLinkProps } from './testingHelpers'
import QuoteNotesLayout from './QuoteNotes'
import { formatDayDate } from '../../utils/dayDate'
import { itemQuantity, itemCount } from '../../../../shared/item-count.js'
import QuantityBadge from '../shared/QuantityBadge.jsx'
import DealLink from '../shared/DealLink.jsx'
import ReceivingExtras from './ReceivingExtras'
import CustomerDealsPanel from './CustomerDealsPanel'

// Design System (matching original warehouse-system)
const theme = {
  colors: {
    charcoal: '#222725',
    sunburst: '#FFD44D',
    greenOnions: '#3BB273',
    imBlue: '#4E8098',
    crimson: '#E5625E',
    porcelain: '#F2EDE4',
    white: '#FFFFFF',
    grey100: '#F8F9F8',
    grey200: '#E7E5E4',
    grey300: '#D6D3D1',
    grey400: '#A8A29E',
    grey500: '#78716C',
    greenLight: '#E8F5EE',
    amberLight: '#FFF9E6',
    redLight: '#FCEEED',
    purple: '#8B5CF6',
    purpleLight: '#F5F3FF'
  },
  fonts: { sans: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif" },
  radius: { sm: '6px', md: '10px', lg: '16px' },
  shadows: { sm: '0 1px 2px rgba(0,0,0,0.05)', md: '0 4px 12px rgba(0,0,0,0.08)' }
}

// Components
const Card = ({ children, style = {}, padding = true, onClick }) => (
  <div onClick={onClick} style={{
    background: theme.colors.white,
    borderRadius: theme.radius.lg,
    boxShadow: theme.shadows.sm,
    border: `1px solid ${theme.colors.grey200}`,
    padding: padding ? '24px' : 0,
    ...style
  }}>
    {children}
  </div>
)

const Badge = ({ children, color = 'grey' }) => {
  const colors = {
    grey: { bg: theme.colors.grey200, text: theme.colors.grey500 },
    green: { bg: theme.colors.greenLight, text: theme.colors.greenOnions },
    amber: { bg: theme.colors.amberLight, text: '#B8860B' },
    red: { bg: theme.colors.redLight, text: theme.colors.crimson },
    purple: { bg: theme.colors.purpleLight, text: theme.colors.purple }
  }
  const c = colors[color] || colors.grey
  return (
    <span style={{
      display: 'inline-flex',
      padding: '4px 10px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: 600,
      background: c.bg,
      color: c.text
    }}>
      {children}
    </span>
  )
}

const Button = ({ children, variant = 'primary', size = 'md', fullWidth = false, disabled = false, onClick }) => {
  const variants = {
    primary: { background: theme.colors.sunburst, color: theme.colors.charcoal },
    secondary: { background: theme.colors.white, color: theme.colors.charcoal, border: `1px solid ${theme.colors.grey300}` },
    success: { background: theme.colors.greenOnions, color: theme.colors.white },
    ghost: { background: 'transparent', color: theme.colors.grey500 }
  }
  const sizes = {
    sm: { padding: '8px 14px', fontSize: '13px' },
    md: { padding: '12px 20px', fontSize: '14px' },
    lg: { padding: '16px 28px', fontSize: '16px' }
  }
  const v = variants[variant]
  const s = sizes[size]
  return (
    <button onClick={onClick} disabled={disabled} style={{
      ...v, ...s,
      border: v.border || 'none',
      borderRadius: theme.radius.md,
      fontWeight: 600,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      width: fullWidth ? '100%' : 'auto',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px'
    }}>
      {children}
    </button>
  )
}

const Spinner = ({ size = 20, light = false }) => (
  <div style={{
    width: size, height: size,
    border: `2px solid ${light ? 'rgba(255,255,255,0.3)' : theme.colors.grey200}`,
    borderTopColor: light ? 'white' : theme.colors.charcoal,
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite'
  }}>
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
)

const Alert = ({ type = 'warning', children }) => {
  const types = {
    info: { bg: theme.colors.grey100, border: theme.colors.grey300, icon: 'ℹ️' },
    warning: { bg: theme.colors.amberLight, border: theme.colors.sunburst, icon: '⚠️' },
    error: { bg: theme.colors.redLight, border: theme.colors.crimson, icon: '✕' }
  }
  const t = types[type]
  return (
    <div style={{
      background: t.bg,
      border: `1px solid ${t.border}`,
      borderRadius: theme.radius.md,
      padding: '12px 16px',
      fontSize: '14px',
      display: 'flex',
      gap: '10px',
      marginBottom: '20px'
    }}>
      <span>{t.icon}</span>
      <div>{children}</div>
    </div>
  )
}

/**
 * The submit button says what one press will do (spec 20260916). The counts
 * are items, not lines (spec 20261008).
 */
function submitLabel(received, notArrived, boxNotArrived) {
  const parts = []
  if (received > 0) parts.push(`${received} received`)
  if (notArrived > 0) parts.push(`${notArrived} not arrived`)
  if (boxNotArrived) parts.push('box not arrived')
  if (parts.length === 0) return 'Confirm items received'
  if (received > 0 && parts.length === 1) return `Confirm ${received} item${received !== 1 ? 's' : ''} received`
  return `${received > 0 ? 'Confirm' : 'Record'} ${parts.join(', ')}`
}

function Receiving() {
  const navigate = useNavigate()
  const [searchType, setSearchType] = useState('dealId')
  const [searchQuery, setSearchQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [searchResults, setSearchResults] = useState([])
  const [selectedDeal, setSelectedDeal] = useState(null)
  const [selectedItems, setSelectedItems] = useState([])
  // What did not arrive (spec 20260916): items the customer never sent, and a
  // box of the shipment that is still missing.
  const [notArrivedItemIds, setNotArrivedItemIds] = useState([])
  const [boxNotArrived, setBoxNotArrived] = useState(false)
  // Short delivery (spec 20261008): how many of a ticked line of several
  // actually arrived, by item id. Absent means the whole line arrived.
  const [arrivedCounts, setArrivedCounts] = useState({})
  // Items found in the boxes that are not on the deal (Trac #851). Each is
  // already a received quote line; Remove records it as not arrived on confirm.
  const [extras, setExtras] = useState([])
  const [step, setStep] = useState('search') // search, results, confirm, success, returnInspect, returnDone
  const [result, setResult] = useState(null)
  const [returnCase, setReturnCase] = useState(null)
  const [issueNote, setIssueNote] = useState('')
  const [inspecting, setInspecting] = useState(false)
  const [scanning, setScanning] = useState(false)
  // A deal the search found but cannot receive: the screen says why and shows
  // the same customer's other deals.
  const [notActionable, setNotActionable] = useState(null)
  const fileInputRef = useRef(null)

  // Navigate to testing with the deal ID
  const handleTestNow = () => {
    const testDealId = result?.action === 'split' ? result.newDealId : result?.dealId
    if (testDealId) {
      navigate(`/warehouse/testing?dealId=${testDealId}`)
    }
  }

  async function resizeImage(file, maxEdge = 1600) {
    const objectUrl = URL.createObjectURL(file)
    try {
      const img = new Image()
      img.src = objectUrl
      await new Promise((resolve, reject) => { img.onload = resolve; img.onerror = reject })
      const scale = Math.min(1, maxEdge / Math.max(img.width, img.height))
      const w = Math.round(img.width * scale)
      const h = Math.round(img.height * scale)
      const canvas = document.createElement('canvas')
      canvas.width = w
      canvas.height = h
      canvas.getContext('2d').drawImage(img, 0, 0, w, h)
      return await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.85))
    } finally {
      URL.revokeObjectURL(objectUrl)
    }
  }

  const handleScanLabel = async (file) => {
    if (!file) return
    setScanning(true)
    setError(null)
    setNotActionable(null)
    setSearchResults([])
    setReturnCase(null)
    try {
      const blob = await resizeImage(file)
      const ocrRes = await fetch('/api/warehouse/receiving/ocr-deal-id', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'image/jpeg' },
        body: blob,
      })
      if (!ocrRes.ok) {
        const text = await ocrRes.text().catch(() => '')
        throw new Error(text || `OCR failed (${ocrRes.status})`)
      }
      const { candidates = [] } = await ocrRes.json()
      if (candidates.length === 0) {
        setError('No deal ID detected on label. Try retaking the photo or enter manually.')
        return
      }
      let found = null
      for (const candidate of candidates) {
        const response = await api.get(`/api/warehouse/receiving/search?q=${encodeURIComponent(candidate)}&type=dealId`)
        const deals = response.deals || []
        if (response.notActionable && !found) found = { candidate, why: response.notActionable }
        if (deals.length > 0) {
          setSearchQuery(candidate)
          if (deals.length === 1) {
            selectDeal(deals[0])
          } else {
            setSearchResults(deals)
            setStep('results')
          }
          return
        }
      }
      if (found) {
        setSearchQuery(found.candidate)
        setNotActionable(found.why)
        setError(found.why.reason)
        return
      }
      setError(`No deal in "Shipping Arranged" matched the label. Tried: ${candidates.join(', ')}. The deal may already be received, or try retaking the photo.`)
    } catch (err) {
      setError(err.message || 'Scan failed')
    } finally {
      setScanning(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handleSearch = async () => {
    if (!searchQuery.trim()) return
    setLoading(true)
    setError(null)
    setNotActionable(null)
    setSearchResults([])
    setReturnCase(null)

    // Order ID search: lookup returns/cases instead of deals
    if (searchType === 'orderId') {
      try {
        const response = await api.get(`/api/orders/returns/by-order-name/${encodeURIComponent(searchQuery.trim())}`)
        if (!response.found) {
          setError('No return found for this order.')
        } else {
          setReturnCase(response.returnRequest)
          setStep('returnInspect')
        }
      } catch (err) {
        setError(err.message || 'Lookup failed')
      } finally {
        setLoading(false)
      }
      return
    }

    try {
      const response = await api.get(`/api/warehouse/receiving/search?q=${encodeURIComponent(searchQuery)}&type=${searchType}`)
      const deals = response.deals || []
      if (deals.length === 0) {
        if (response.notActionable) {
          setNotActionable(response.notActionable)
          setError(response.notActionable.reason)
        } else if (response.inconclusive) {
          setError(response.note)
        } else if (searchType === 'dealId') {
          setError(`No deal matches "${searchQuery.trim()}". There is no customer to look up.`)
        } else {
          setError('No deals found in "Shipping Arranged" status.')
        }
      } else if (deals.length === 1) {
        selectDeal(deals[0])
      } else {
        setSearchResults(deals)
        setStep('results')
      }
    } catch (err) {
      setError(err.message || 'Search failed')
    } finally {
      setLoading(false)
    }
  }

  const selectDeal = (deal) => {
    setSelectedDeal(deal)
    setSelectedItems([])
    setNotArrivedItemIds([])
    setBoxNotArrived(false)
    setArrivedCounts({})
    setExtras([])
    setStep('confirm')
  }

  // Receive on a panel row: look the deal up afresh, so a row that went stale cannot be received.
  const openFromPanel = async (row) => {
    setLoading(true)
    setError(null)
    try {
      const response = await api.get(`/api/warehouse/receiving/search?q=${encodeURIComponent(row.id)}&type=dealId`)
      const [found] = response.deals || []
      if (found) {
        setNotActionable(null)
        selectDeal(found)
      } else {
        setError(response.notActionable?.reason || `${row.reference} can no longer be received.`)
      }
    } catch (err) {
      setError(err.message || 'Search failed')
    } finally {
      setLoading(false)
    }
  }

  const forgetArrivedCount = (id) => {
    setArrivedCounts(prev => {
      const { [id]: _dropped, ...rest } = prev
      return rest
    })
  }

  const toggleItem = (id) => {
    setSelectedItems(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id])
    // An item cannot be both received and not arrived.
    setNotArrivedItemIds(prev => prev.filter(i => i !== id))
    forgetArrivedCount(id)
  }

  const setArrivedCount = (item, value) => {
    setArrivedCounts(prev => ({ ...prev, [item.id]: Number(value) }))
  }

  const arrivedCount = (item) => arrivedCounts[item.id] ?? itemQuantity(item)

  // What the worker lowers before it receives: ticked lines short of the quote.
  const shortDeliveries = () => (selectedDeal?.items || [])
    .filter(i => selectedItems.includes(i.id) && arrivedCount(i) < itemQuantity(i))

  const toggleNotArrived = (id) => {
    setNotArrivedItemIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id])
    setSelectedItems(prev => prev.filter(i => i !== id))
  }

  const selectAllItems = () => {
    if (selectedDeal?.items) {
      setSelectedItems(selectedDeal.items.filter(i => !notArrivedItemIds.includes(i.id)).map(i => i.id))
    }
  }

  const reset = () => {
    setSearchQuery('')
    setSearchResults([])
    setSelectedDeal(null)
    setNotActionable(null)
    setSelectedItems([])
    setNotArrivedItemIds([])
    setBoxNotArrived(false)
    setArrivedCounts({})
    setExtras([])
    setError(null)
    setStep('search')
    setResult(null)
    setReturnCase(null)
    setIssueNote('')
    setInspecting(false)
  }

  const handleSubmit = async () => {
    if (selectedItems.length === 0 && notArrivedItemIds.length === 0 && !boxNotArrived) {
      setError('Tick what arrived, mark what did not, or record the missing box.')
      return
    }
    setLoading(true)
    setError(null)
    try {
      // One call does all three (spec 20260916): the worker receives, then
      // rejects the not-arrived items, then records the box wait.
      const response = await api.post('/api/warehouse/receiving/mark-received', {
        dealId: selectedDeal.id,
        receivedItemIds: selectedItems,
        receivedQuantities: Object.fromEntries(shortDeliveries().map(i => [i.id, arrivedCount(i)])),
        notArrivedItemIds,
        boxNotArrived,
        allItems: selectedDeal.items,
        isListingService: selectedDeal.isListingService || false
      })
      setResult(response)
      setStep('success')
    } catch (err) {
      setError(err.message || 'Failed to mark as received')
    } finally {
      setLoading(false)
    }
  }

  // Search Screen
  if (step === 'search') {
    return (
      <div style={{ maxWidth: '480px', margin: '60px auto', padding: '0 24px' }}>
        <Card style={{ padding: '40px' }}>
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <div style={{ fontSize: '56px', marginBottom: '16px' }}>📥</div>
            <h2 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '8px' }}>Goods Receiving</h2>
            <p style={{ color: theme.colors.grey500 }}>Search for shipments to receive</p>
          </div>

          <div style={{
            display: 'flex', gap: '4px', marginBottom: '24px',
            background: theme.colors.grey100, borderRadius: theme.radius.md, padding: '4px'
          }}>
            {['dealId', 'postcode', 'orderId'].map(t => (
              <button
                key={t}
                onClick={() => { setSearchType(t); setSearchQuery(''); setError(null) }}
                style={{
                  flex: 1, padding: '10px', border: 'none',
                  borderRadius: theme.radius.sm,
                  background: searchType === t ? theme.colors.white : 'transparent',
                  fontWeight: searchType === t ? 600 : 400,
                  cursor: 'pointer', fontSize: '13px'
                }}
              >
                {t === 'dealId' ? 'Deal ID' : t === 'postcode' ? 'Postcode' : 'Order ID'}
              </button>
            ))}
          </div>

          <div style={{ marginBottom: '20px' }}>
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyPress={e => e.key === 'Enter' && handleSearch()}
              placeholder={searchType === 'dealId' ? 'Enter Deal ID' : searchType === 'postcode' ? 'Enter Postcode' : 'Enter Order ID (e.g. AT-GB-4683)'}
              style={{
                width: '100%', padding: '14px 16px',
                border: `1px solid ${theme.colors.grey300}`,
                borderRadius: theme.radius.md, fontSize: '15px',
                fontFamily: searchType === 'dealId' ? theme.fonts.sans : 'inherit',
                textAlign: 'center', outline: 'none'
              }}
            />
          </div>

          {error && <Alert type="error">{error}</Alert>}

          <Button onClick={handleSearch} disabled={loading || scanning || !searchQuery.trim()} fullWidth size="lg">
            {loading ? <><Spinner size={18} /> Searching...</> : 'Search'}
          </Button>

          {searchType === 'dealId' && (
            <div style={{ marginTop: '12px' }}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                style={{ display: 'none' }}
                onChange={e => handleScanLabel(e.target.files?.[0])}
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={loading || scanning}
                fullWidth
                size="lg"
                variant="secondary"
              >
                {scanning ? <><Spinner size={18} /> Scanning label...</> : '📷 Scan label'}
              </Button>
            </div>
          )}
        </Card>
        {notActionable && (
          <div style={{ marginTop: '16px' }}>
            <CustomerDealsPanel dealId={notActionable.dealId} screen="receiving" onAct={openFromPanel} />
          </div>
        )}
      </div>
    )
  }

  // Results Screen (multiple deals found)
  if (step === 'results') {
    return (
      <div style={{ maxWidth: '600px', margin: '40px auto', padding: '0 24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 700 }}>
            {searchResults.length} Deal{searchResults.length !== 1 ? 's' : ''} Found
          </h2>
          <Button variant="secondary" size="sm" onClick={reset}>New Search</Button>
        </div>

        {searchResults.map(deal => (
          <Card
            key={deal.id}
            onClick={() => selectDeal(deal)}
            style={{ marginBottom: '12px', cursor: 'pointer' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontFamily: theme.fonts.sans, fontSize: '13px', color: theme.colors.grey500, marginBottom: '4px' }}>
                  <DealLink {...dealLinkProps(deal)} newTab />
                </div>
                <div style={{ fontWeight: 600, fontSize: '16px', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {deal.customerName || deal.dealName}
                  {deal.isListingService && <Badge color="purple">Consignment</Badge>}
                </div>
                <div style={{ fontSize: '14px', color: theme.colors.grey500 }}>
                  {deal.items?.length || 0} item{(deal.items?.length || 0) !== 1 ? 's' : ''} • {deal.boxCount || 1} box{(deal.boxCount || 1) !== 1 ? 'es' : ''}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                {deal.postcode && <Badge>{deal.postcode}</Badge>}
              </div>
            </div>
          </Card>
        ))}
      </div>
    )
  }

  // Confirm Screen
  if (step === 'confirm' && selectedDeal) {
    const items = selectedDeal.items || []
    const boxCount = selectedDeal.boxCount || 1
    // Counts are items (sum of quantity), not lines (spec 20261008).
    const remaining = itemCount(items.filter(i => !selectedItems.includes(i.id) && !notArrivedItemIds.includes(i.id)))
    const selectedUnits = items.filter(i => selectedItems.includes(i.id)).reduce((n, i) => n + arrivedCount(i), 0)
    const notArrivedUnits = itemCount(items.filter(i => notArrivedItemIds.includes(i.id)))
    const totalUnits = itemCount(items)
    const extraUnits = extras.reduce((n, extra) => n + extra.quantity, 0)
    const short = shortDeliveries()
    const payloadEmpty = selectedItems.length === 0 && notArrivedItemIds.length === 0 && !boxNotArrived
    return (
      // The card stays centred; internal notes and other deals sit in the space to its left.
      <QuoteNotesLayout dealId={selectedDeal.id} mainWidth={600} aside={<CustomerDealsPanel dealId={selectedDeal.id} screen="receiving" onAct={openFromPanel} />} style={{ margin: '40px 0', padding: '0 24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <div style={{ fontFamily: theme.fonts.sans, fontSize: '13px', color: theme.colors.grey500, marginBottom: '4px' }}>
              <DealLink {...dealLinkProps(selectedDeal)} newTab />
            </div>
            <h2 style={{ fontSize: '20px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '12px' }}>
              {selectedDeal.customerName || selectedDeal.dealName}
              {selectedDeal.isListingService && <Badge color="purple">Consignment</Badge>}
            </h2>
          </div>
          <Button variant="secondary" size="sm" onClick={reset}>New Search</Button>
        </div>

        {/* Deal Info */}
        <Card style={{ marginBottom: '20px', padding: '16px 20px' }}>
          <div style={{ display: 'flex', gap: '32px', alignItems: 'center', flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: '13px', color: theme.colors.grey500 }}>Boxes</div>
              <div style={{ fontSize: '24px', fontWeight: 700, fontFamily: theme.fonts.sans }}>{boxCount}</div>
            </div>
            {selectedDeal.carrier && (
              <div>
                <div style={{ fontSize: '13px', color: theme.colors.grey500 }}>Carrier</div>
                <div style={{ fontSize: '16px', fontWeight: 600 }}>{selectedDeal.carrier}</div>
              </div>
            )}
            {selectedDeal.trackingNumber && (
              <div>
                <div style={{ fontSize: '13px', color: theme.colors.grey500 }}>Tracking</div>
                <div style={{ fontSize: '14px', fontFamily: theme.fonts.sans }}>{selectedDeal.trackingNumber}</div>
              </div>
            )}
          </div>
        </Card>

        {/* A multi-box trade is where "not arrived" is most often wrong: the
            item is in the other box. Say so before the receiver records
            anything as missing (spec 20260916). */}
        {boxCount > 1 && (
          <Alert type="info">
            This trade was sent as {boxCount} boxes. Check the other boxes before you record anything as missing.
          </Alert>
        )}
        {selectedDeal.boxWaitUntil && (
          <Alert type="warning">
            Box not arrived recorded. We wait for it until {formatDayDate(selectedDeal.boxWaitUntil, { long: true })}.
          </Alert>
        )}

        {/* Items Selection */}
        <Card padding={false} style={{ marginBottom: '20px' }}>
          <div style={{
            padding: '16px 20px',
            borderBottom: `1px solid ${theme.colors.grey200}`,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center'
          }}>
            <div>
              <div style={{ fontWeight: 600 }}>
                Items ({selectedUnits}/{totalUnits} selected)
              </div>
              {totalUnits !== items.length && (
                <div style={{ fontSize: '13px', color: theme.colors.grey500 }}>
                  {totalUnits} items on {items.length} line{items.length !== 1 ? 's' : ''}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button variant="ghost" size="sm" onClick={selectAllItems}>Select All</Button>
              {selectedItems.length > 0 && (
                <Button variant="ghost" size="sm" onClick={() => setSelectedItems([])}>Clear</Button>
              )}
            </div>
          </div>

          <div style={{ padding: '12px' }}>
            {items.map(item => {
              const isSelected = selectedItems.includes(item.id)
              const isMarked = notArrivedItemIds.includes(item.id)
              const expectedQty = Number(item.quantity) || 1
              return (
                <div key={item.id} style={{ marginBottom: '8px' }}>
                  <div
                    onClick={isMarked ? undefined : () => toggleItem(item.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '14px',
                      padding: '14px',
                      background: isSelected ? theme.colors.greenLight : theme.colors.grey100,
                      border: `2px solid ${isSelected ? theme.colors.greenOnions : 'transparent'}`,
                      borderRadius: theme.radius.md,
                      cursor: isMarked ? 'default' : 'pointer',
                      opacity: isMarked ? 0.6 : 1,
                      transition: 'all 0.15s ease'
                    }}
                  >
                    <div style={{
                      width: '24px', height: '24px', borderRadius: '50%',
                      background: isSelected ? theme.colors.greenOnions : theme.colors.white,
                      border: isSelected ? 'none' : `2px solid ${theme.colors.grey300}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: theme.colors.white, fontSize: '14px', flexShrink: 0
                    }}>
                      {isSelected && '✓'}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500, textDecoration: isMarked ? 'line-through' : 'none' }}>{item.name || item.productName}</div>
                      {/* Consignment: there IS no buy — we sell for the customer
                          (spec §6: BP never surfaces on the consignment path). */}
                      {selectedDeal.isListingService ? (
                        item.sellPrice && (
                          <div style={{ fontSize: '13px', color: theme.colors.grey500, marginTop: '2px' }}>
                            Consignment item - Lists at: {formatCurrency(item.sellPrice)}
                          </div>
                        )
                      ) : (item.sellPrice || item.buyPrice) && (
                        <div style={{ fontSize: '13px', color: theme.colors.grey500, marginTop: '2px' }}>
                          {item.buyPrice && <span>Buy: {formatCurrency(item.buyPrice)}</span>}
                          {item.buyPrice && item.sellPrice && <span> • </span>}
                          {item.sellPrice && <span>Sell: {formatCurrency(item.sellPrice)}</span>}
                        </div>
                      )}
                    </div>
                    {/* Expected unit count for this line (the quote line Quantity).
                        Staff verify incoming stock against it, so it shows on every
                        line — a bare card would leave "is it 1, or just not shown?"
                        open. A count above 1 is what must catch the eye, so it gets
                        the amber badge; a single unit stays quiet grey. */}
                    {isMarked ? (
                      <Badge color="grey">Not arrived</Badge>
                    ) : expectedQty > 1 ? (
                      <QuantityBadge quantity={expectedQty} />
                    ) : (
                      <span style={{ fontSize: '13px', color: theme.colors.grey500, flexShrink: 0, whiteSpace: 'nowrap' }}>
                        1 unit
                      </span>
                    )}
                    {isMarked ? (
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); toggleNotArrived(item.id) }}>Undo</Button>
                    ) : !isSelected && (
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); toggleNotArrived(item.id) }}>Not arrived</Button>
                    )}
                  </div>
                  {/* Short delivery: a ticked line of several can record fewer
                      arrived. The worker lowers the line before it receives it. */}
                  {isSelected && expectedQty > 1 && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: theme.colors.grey500, padding: '6px 14px 0' }}>
                      Arrived
                      {/* 1 to the quote's count only: more than the quote holds is a
                          pricing question, and none arrived is "Not arrived". */}
                      <select
                        value={arrivedCount(item)}
                        onChange={e => setArrivedCount(item, e.target.value)}
                        aria-label={`Arrived of ${expectedQty}`}
                        style={{ padding: '6px 8px', border: `1px solid ${theme.colors.grey300}`, borderRadius: theme.radius.sm, fontSize: '14px', background: theme.colors.white }}
                      >
                        {Array.from({ length: expectedQty }, (_, n) => expectedQty - n).map(n => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </select>
                      of {expectedQty}
                    </label>
                  )}
                  {isMarked && (
                    <div style={{ fontSize: '13px', color: theme.colors.grey500, padding: '6px 14px 0' }}>
                      The customer did not send this. Reopening it later takes an operator, and a paid deal may already have closed. If a box may still be coming, use Box not arrived instead.
                    </div>
                  )}
                </div>
              )
            })}
          </div>
          {!selectedDeal.isListingService && (
            <ReceivingExtras
              quoteId={selectedDeal.id}
              extras={extras}
              onAdded={extra => setExtras(prev => [...prev, extra])}
              onRemoved={id => setExtras(prev => prev.filter(extra => extra.id !== id))}
            />
          )}
        </Card>

        {/* Box not arrived: the rest of the shipment is still on its way, or
            lost. Records a wait on the quote; payment holds for three
            working days. Available whatever the box count says, because the
            count is parsed from free text and defaults to 1. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
          <Button variant={boxNotArrived ? 'primary' : 'secondary'} size="sm" onClick={() => setBoxNotArrived(v => !v)}>
            {boxNotArrived ? '✓ Box not arrived' : 'Box not arrived'}
          </Button>
          <span style={{ fontSize: '13px', color: theme.colors.grey500 }}>
            {boxNotArrived ? 'On. Press again to undo.' : 'A box of this trade has not turned up.'}
          </span>
        </div>

        {error && <Alert type="error">{error}</Alert>}

        {short.length > 0 && (
          <Alert type="warning">
            {short.map(i => (
              <div key={i.id}>
                Short delivery: {i.name || i.productName} drops from {itemQuantity(i)} to {arrivedCount(i)}. The quote then pays for {arrivedCount(i)}.
              </div>
            ))}
          </Alert>
        )}

        {boxNotArrived ? (
          <Alert type="warning">
            We record the missing box and wait 3 working days before paying what has arrived. Tick what is in this box.
          </Alert>
        ) : selectedItems.length > 0 && remaining > 0 && (
          /* Post-cutover, a partial receive does NOT split the deal: the
             un-arrived items simply stay pending on this same quote
             (warehouse-system receiving.js — createSplitDeal is deleted). */
          <Alert type="warning">
            Partial receipt: {remaining === 1
              ? 'the other item stays on this deal as not yet arrived. Receive it here when it turns up.'
              : `the other ${remaining} items stay on this deal as not yet arrived. Receive them here when they turn up.`}
          </Alert>
        )}

        <Button
          variant="success"
          onClick={handleSubmit}
          disabled={loading || payloadEmpty}
          fullWidth
          size="lg"
        >
          {loading ? <><Spinner size={18} light /> Processing...</> : submitLabel(selectedUnits + extraUnits, notArrivedUnits, boxNotArrived)}
        </Button>
      </QuoteNotesLayout>
    )
  }

  // Success Screen
  if (step === 'success') {
    const receivedCount = result?.receivedItems?.length || 0
    return (
      <div style={{ maxWidth: '480px', margin: '60px auto', padding: '0 24px' }}>
        <Card style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{ fontSize: '64px', marginBottom: '16px' }}>✅</div>
          <h2 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '12px' }}>
            {receivedCount > 0 ? 'Items Received' : 'Recorded'}
          </h2>
          <p style={{ color: theme.colors.grey500, marginBottom: '24px' }}>
            {result?.message || 'Items have been marked as received successfully.'}
          </p>

          {/* A step that failed after the receive is said here, loudly: the
              receive stuck, and the receiver must know what did not. */}
          {(result?.failures || []).map(failure => (
            <Alert key={failure} type="error">{failure}</Alert>
          ))}

          {result && (
            <div style={{
              background: theme.colors.grey100,
              borderRadius: theme.radius.md,
              padding: '16px',
              textAlign: 'left',
              marginBottom: '24px'
            }}>
              {result.boxWaitUntil ? (
                <div>
                  <div style={{ fontSize: '13px', color: theme.colors.grey500 }}>Waiting for the other box until</div>
                  <div style={{ fontWeight: 600 }}>{formatDayDate(result.boxWaitUntil, { long: true })}</div>
                </div>
              ) : result.remainingCount > 0 ? (
                <div>
                  <div style={{ fontSize: '13px', color: theme.colors.grey500 }}>Still to arrive on this deal</div>
                  {/* Items; a worker from before spec 20261008 sends lines only. */}
                  <div style={{ fontWeight: 600 }}>
                    {result.remainingItemCount != null
                      ? `${result.remainingItemCount} item${result.remainingItemCount !== 1 ? 's' : ''}`
                      : result.remainingCount}
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: '13px', color: theme.colors.grey500 }}>Deal moved to Arrived to Test</div>
                  <div style={{ fontFamily: theme.fonts.sans, fontWeight: 600 }}><DealLink {...dealLinkProps(selectedDeal)} newTab /></div>
                </div>
              )}
              {result.notArrivedItems?.length > 0 && (
                <div style={{ marginTop: '12px' }}>
                  <div style={{ fontSize: '13px', color: theme.colors.grey500 }}>Recorded as not arrived</div>
                  {result.notArrivedItems.map(name => (
                    <div key={name} style={{ fontWeight: 600 }}>{name}</div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {receivedCount > 0 && (
              <Button variant="primary" onClick={handleTestNow} fullWidth size="lg">
                Test Now
              </Button>
            )}
            <Button variant="secondary" onClick={reset} fullWidth size="lg">
              Receive Another Shipment
            </Button>
          </div>
        </Card>
      </div>
    )
  }

  // Return Inspection Screen
  if (step === 'returnInspect' && returnCase) {
    const customerName = returnCase.customer_name_extracted || returnCase.from_name || returnCase.from_email || 'Unknown'

    const handleInspect = async (action) => {
      if (action === 'issue' && !issueNote.trim()) {
        setError('Please describe the issue before submitting.')
        return
      }
      setInspecting(true)
      setError(null)
      try {
        const res = await api.post(`/api/orders/returns/${returnCase.id}/warehouse-inspect`, {
          action,
          issueNote: action === 'issue' ? issueNote.trim() : undefined
        })
        setResult(res)
        setStep('returnDone')
      } catch (err) {
        setError(err.message || 'Failed to process')
      } finally {
        setInspecting(false)
      }
    }

    return (
      <div style={{ maxWidth: '480px', margin: '60px auto', padding: '0 24px' }}>
        <Card style={{ padding: '32px' }}>
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>📦</div>
            <h2 style={{ fontSize: '22px', fontWeight: 700, marginBottom: '4px' }}>Return Found</h2>
            <p style={{ color: theme.colors.grey500, fontSize: '14px' }}>Inspect the returned item</p>
          </div>

          <div style={{
            background: theme.colors.grey100, borderRadius: theme.radius.md,
            padding: '16px', marginBottom: '20px', fontSize: '14px', lineHeight: 1.8
          }}>
            <div><strong>Case:</strong> {returnCase.public_id || `#${returnCase.id}`}</div>
            <div><strong>Customer:</strong> {customerName}</div>
            <div><strong>Order:</strong> {returnCase.order_name || '-'}</div>
            {returnCase.product_title && <div><strong>Product:</strong> {returnCase.product_title}</div>}
            <div><strong>Reason:</strong> {returnCase.reason_name || '-'}</div>
            {returnCase.reason_notes && <div><strong>Notes:</strong> {returnCase.reason_notes}</div>}
            <div>
              <strong>Status:</strong>{' '}
              <Badge color={returnCase.status === 'awaiting_refund' ? 'green' : returnCase.status === 'return_issue' ? 'red' : 'amber'}>
                {returnCase.status.replace(/_/g, ' ')}
              </Badge>
            </div>
          </div>

          {error && <Alert type="error">{error}</Alert>}

          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
            <Button
              variant="success"
              onClick={() => handleInspect('accept')}
              disabled={inspecting}
              fullWidth
              size="lg"
            >
              {inspecting ? 'Processing...' : 'Return OK'}
            </Button>
            <Button
              variant="secondary"
              onClick={() => setStep('returnIssueForm')}
              disabled={inspecting}
              fullWidth
              size="lg"
              style={{ background: '#fef2f2', color: '#991b1b', border: '1px solid #fca5a5' }}
            >
              Issue with Item
            </Button>
          </div>

          <Button variant="ghost" onClick={reset} fullWidth>
            New Search
          </Button>
        </Card>
      </div>
    )
  }

  // Return Issue Form
  if (step === 'returnIssueForm' && returnCase) {
    const handleSubmitIssue = async () => {
      if (!issueNote.trim()) {
        setError('Please describe the issue.')
        return
      }
      setInspecting(true)
      setError(null)
      try {
        const res = await api.post(`/api/orders/returns/${returnCase.id}/warehouse-inspect`, {
          action: 'issue',
          issueNote: issueNote.trim()
        })
        setResult(res)
        setStep('returnDone')
      } catch (err) {
        setError(err.message || 'Failed to log issue')
      } finally {
        setInspecting(false)
      }
    }

    return (
      <div style={{ maxWidth: '480px', margin: '60px auto', padding: '0 24px' }}>
        <Card style={{ padding: '32px' }}>
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>⚠️</div>
            <h2 style={{ fontSize: '22px', fontWeight: 700, marginBottom: '4px' }}>Report Issue</h2>
            <p style={{ color: theme.colors.grey500, fontSize: '14px' }}>
              {returnCase.public_id} — {returnCase.product_title || returnCase.order_name}
            </p>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>
              What is the issue?
            </label>
            <textarea
              value={issueNote}
              onChange={e => setIssueNote(e.target.value)}
              placeholder="Describe the issue (e.g. scratched, missing parts, different condition...)"
              rows={4}
              style={{
                width: '100%', padding: '12px', border: `1px solid ${theme.colors.grey300}`,
                borderRadius: theme.radius.md, fontSize: '14px', resize: 'vertical',
                fontFamily: 'inherit', outline: 'none'
              }}
            />
          </div>

          {error && <Alert type="error">{error}</Alert>}

          <div style={{ display: 'flex', gap: '12px' }}>
            <Button variant="ghost" onClick={() => { setStep('returnInspect'); setError(null) }} fullWidth>
              Back
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmitIssue}
              disabled={inspecting || !issueNote.trim()}
              fullWidth
              size="lg"
              style={{ background: '#dc2626' }}
            >
              {inspecting ? 'Submitting...' : 'Log Issue'}
            </Button>
          </div>
        </Card>
      </div>
    )
  }

  // Return Done Screen
  if (step === 'returnDone') {
    const wasAccepted = result?.message?.includes('accepted') || result?.message?.includes('Awaiting Refund')
    return (
      <div style={{ maxWidth: '480px', margin: '60px auto', padding: '0 24px' }}>
        <Card style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{ fontSize: '64px', marginBottom: '16px' }}>{wasAccepted ? '✅' : '⚠️'}</div>
          <h2 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '12px' }}>
            {wasAccepted ? 'Return Accepted' : 'Issue Logged'}
          </h2>
          <p style={{ color: theme.colors.grey500, marginBottom: '24px' }}>
            {result?.message || 'Done.'}
          </p>
          <Button variant="secondary" onClick={reset} fullWidth size="lg">
            Process Another Return
          </Button>
        </Card>
      </div>
    )
  }

  return null
}

export default Receiving
